import React, { useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

const SORT_DIRECTION_INDICATOR = { asc: " ↑", desc: " ↓" };

// Rows per page for the tables that opt in. Exported rather than defaulted so pagination
// stays something a screen asks for: dropping the prop turns it off for that table alone,
// and passing a literal gives one table a different size, while this keeps the shared
// choice in one place.
export const DEFAULT_PAGE_SIZE = 50;

function columnHeaderLabel(header) {
  const headerDef = header.column.columnDef.header;
  return typeof headerDef === "string" ? headerDef : header.column.id;
}

function renderHeaderContent(header) {
  return flexRender(header.column.columnDef.header, header.getContext());
}

function sortDirectionIndicator(sorted) {
  return SORT_DIRECTION_INDICATOR[sorted] ?? "";
}

function renderColumnHeader(header) {
  if (header.isPlaceholder) {
    return null;
  }

  if (header.column.columnDef.enableSorting === false) {
    return (
      <span className="inline-flex items-center gap-1 font-semibold">
        {renderHeaderContent(header)}
      </span>
    );
  }

  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 font-semibold text-left hover:underline"
      onClick={header.column.getToggleSortingHandler()}
    >
      {renderHeaderContent(header)}
      {sortDirectionIndicator(header.column.getIsSorted())}
    </button>
  );
}

const FILTER_CONTROL_CLASS_NAME =
  "w-full min-w-0 rounded border border-[var(--color-border)] py-1 text-xs font-normal";
const FILTER_INPUT_CLASS_NAME = `${FILTER_CONTROL_CLASS_NAME} px-2`;
// The native select arrow is painted by the browser and ignores padding, so it sits
// flush against the border. Hide it and draw our own chevron, inset from the edge.
const FILTER_SELECT_CLASS_NAME = `${FILTER_CONTROL_CLASS_NAME} appearance-none bg-transparent pl-2 pr-8 text-foreground`;

function FilterSelectChevron() {
  return (
    <svg
      viewBox="0 0 20 20"
      aria-hidden="true"
      className="pointer-events-none absolute top-1/2 right-2.5 size-3.5 -translate-y-1/2 text-muted"
    >
      <path
        d="M5.5 7.5 10 12l4.5-4.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Options for a select filter: the column's distinct values as they appear in the data,
// so a table never offers a choice that matches nothing.
function selectFilterOptions(column) {
  return Array.from(column.getFacetedUniqueValues().keys())
    .filter((value) => value !== null && value !== undefined && value !== "")
    .map(String)
    .sort((a, b) => a.localeCompare(b));
}

// A column opts in with `meta: { filterVariant: "select" }` and an exact-match `filterFn`
// (e.g. "equalsString"). Text filtering is a substring match, which is wrong for enum-like
// columns whose labels contain each other ("Active" is inside "Inactive").
function renderSelectFilter(header) {
  return (
    <>
      <label className="sr-only" htmlFor={`filter-${header.id}`}>
        Filter {columnHeaderLabel(header)}
      </label>
      <div className="relative min-w-0">
        <select
          id={`filter-${header.id}`}
          className={FILTER_SELECT_CLASS_NAME}
          value={header.column.getFilterValue() ?? ""}
          onChange={(e) =>
            header.column.setFilterValue(e.target.value || undefined)
          }
        >
          <option value="">All</option>
          {selectFilterOptions(header.column).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <FilterSelectChevron />
      </div>
    </>
  );
}

function renderColumnFilter(header) {
  if (header.column.columnDef.enableColumnFilter === false) {
    return <span className="block min-h-[1.75rem]" aria-hidden="true" />;
  }

  if (header.column.columnDef.meta?.filterVariant === "select") {
    return renderSelectFilter(header);
  }

  return (
    <>
      <label className="sr-only" htmlFor={`filter-${header.id}`}>
        Filter {columnHeaderLabel(header)}
      </label>
      <input
        id={`filter-${header.id}`}
        type="text"
        className={FILTER_INPUT_CLASS_NAME}
        placeholder="Filter"
        value={header.column.getFilterValue() ?? ""}
        onChange={(e) => header.column.setFilterValue(e.target.value)}
      />
    </>
  );
}

function renderTableBody(flatRows, columns, emptyMessage) {
  if (flatRows.length === 0) {
    return (
      <tr>
        <td colSpan={columns.length} className="text-muted py-6">
          {emptyMessage}
        </td>
      </tr>
    );
  }

  return flatRows.map((row) => (
    <tr key={row.id}>
      {row.getVisibleCells().map((cell) => (
        <td key={cell.id}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      ))}
    </tr>
  ));
}

// Renders the row range and page nav for a paginated table. The range describes the
// *filtered* row set, so it tracks the filters above rather than the full data set. The
// nav is hidden on a single page, where prev/next would only ever be disabled, but the
// summary stays because the total is useful on its own.
function renderPagination(table, filteredRowCount) {
  const { pageIndex, pageSize } = table.getState().pagination;
  const pageCount = table.getPageCount();
  const firstRow = filteredRowCount === 0 ? 0 : pageIndex * pageSize + 1;
  const lastRow = Math.min((pageIndex + 1) * pageSize, filteredRowCount);

  return (
    <div className="table-pagination">
      <p className="table-pagination__summary text-muted" aria-live="polite">
        {filteredRowCount === 0
          ? "No rows"
          : `Showing ${firstRow}–${lastRow} of ${filteredRowCount}`}
      </p>

      {pageCount > 1 && (
        <div className="table-pagination__nav">
          <button
            type="button"
            className="btn btn--secondary"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </button>
          <span className="table-pagination__page">
            Page {pageIndex + 1} of {pageCount}
          </span>
          <button
            type="button"
            className="btn btn--secondary"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default function SortableFilterableTable({
  data,
  columns,
  emptyMessage = "No rows match.",
  ariaLabel = "Data table",
  getRowId,
  pageSize = null,
}) {
  // Opt-in: without a pageSize every row renders, which is how the other tables using this
  // component still behave. Rows are already all in the browser either way — this paginates
  // the rendering, it does not change what the server sends.
  const paginated = Number.isFinite(pageSize) && pageSize > 0;
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);

  const table = useReactTable({
    data,
    columns,
    ...(getRowId ? { getRowId } : {}),
    ...(paginated
      ? {
          getPaginationRowModel: getPaginationRowModel(),
          initialState: { pagination: { pageIndex: 0, pageSize } },
          // Filtering or sorting down to fewer pages while parked on a later page would
          // otherwise show an empty table.
          autoResetPageIndex: true,
        }
      : {}),
    state: {
      sorting,
      globalFilter,
      columnFilters,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    globalFilterFn: (row, _columnId, filterValue) => {
      const search = String(filterValue ?? "")
        .toLowerCase()
        .trim();
      if (!search) return true;
      const haystack = row
        .getVisibleCells()
        .map((cell) => String(cell.getValue() ?? "").toLowerCase())
        .join(" ");
      return haystack.includes(search);
    },
  });

  // getRowModel() is the paginated slice when pagination is on; the filtered total comes from
  // the filtered model so the summary counts matches rather than the current page.
  const flatRows = table.getRowModel().rows;
  const filteredRowCount = table.getFilteredRowModel().rows.length;

  return (
    <div className="sortable-filterable-table">
      <div className="table-wrap">
        <table className="data-table" aria-label={ariaLabel}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} scope="col">
                    {renderColumnHeader(header)}
                  </th>
                ))}
              </tr>
            ))}
            <tr className="data-table__filters">
              {table.getFlatHeaders().map((header) => (
                <th key={`f-${header.id}`} scope="col">
                  {renderColumnFilter(header)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{renderTableBody(flatRows, columns, emptyMessage)}</tbody>
        </table>
      </div>
      {paginated && renderPagination(table, filteredRowCount)}
    </div>
  );
}
